// Self-contained RTL/Hebrew calendar date-picker (no external lib). Registers an
// Alpine component "datepicker" that shows a click-to-open month grid instead of a
// native <input type="date">. The canonical value lives in a hidden <input> as
// yyyy-MM-dd so existing GET filter forms keep working unchanged; the visible field
// is a readonly button — picking a day is the only way to set it (no typing).
import type { Alpine } from "alpinejs";

const MONTHS = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];
// Sunday-first; the popup is dir="rtl" so Sunday renders in the right-most column.
const DOW = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];

const pad2 = (n: number): string => (n < 10 ? "0" + n : String(n));
const toIso = (y: number, m: number, d: number): string => `${y}-${pad2(m + 1)}-${pad2(d)}`;

interface Cell {
  key: string;
  day: number | null;
  iso: string;
  today: boolean;
}

export function registerDatepicker(alpine: Alpine): void {
  // `initial` is a yyyy-MM-dd string (or "" for an empty field).
  alpine.data("datepicker", (initial: string) => ({
    open: false,
    value: initial || "",
    viewYear: 0,
    viewMonth: 0, // 0-based

    init() {
      const base = this.parse(this.value) ?? this.parse(this.todayIso())!;
      this.viewYear = base.y;
      this.viewMonth = base.m;
    },

    parse(s: string): { y: number; m: number; d: number } | null {
      const mm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
      if (!mm) return null;
      return { y: +mm[1], m: +mm[2] - 1, d: +mm[3] };
    },

    todayIso(): string {
      const t = new Date();
      return toIso(t.getFullYear(), t.getMonth(), t.getDate());
    },

    // dd/MM/yyyy for display; empty when no value.
    get display(): string {
      const p = this.parse(this.value);
      return p ? `${pad2(p.d)}/${pad2(p.m + 1)}/${p.y}` : "";
    },

    get monthLabel(): string {
      return `${MONTHS[this.viewMonth]} ${this.viewYear}`;
    },

    dow: DOW,

    get grid(): Cell[] {
      const first = new Date(this.viewYear, this.viewMonth, 1).getDay(); // 0=Sun
      const days = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
      const today = this.todayIso();
      const cells: Cell[] = [];
      for (let i = 0; i < first; i++) {
        cells.push({ key: "b" + i, day: null, iso: "", today: false });
      }
      for (let d = 1; d <= days; d++) {
        const iso = toIso(this.viewYear, this.viewMonth, d);
        cells.push({ key: iso, day: d, iso, today: iso === today });
      }
      while (cells.length % 7 !== 0) {
        cells.push({ key: "t" + cells.length, day: null, iso: "", today: false });
      }
      return cells;
    },

    toggle() {
      this.open = !this.open;
      if (this.open) {
        const p = this.parse(this.value);
        if (p) {
          this.viewYear = p.y;
          this.viewMonth = p.m;
        }
      }
    },

    prevMonth() {
      if (this.viewMonth === 0) {
        this.viewMonth = 11;
        this.viewYear--;
      } else {
        this.viewMonth--;
      }
    },

    nextMonth() {
      if (this.viewMonth === 11) {
        this.viewMonth = 0;
        this.viewYear++;
      } else {
        this.viewMonth++;
      }
    },

    select(cell: Cell) {
      if (!cell.day) return;
      this.value = cell.iso;
      this.open = false;
    },

    goToday() {
      const p = this.parse(this.todayIso())!;
      this.viewYear = p.y;
      this.viewMonth = p.m;
      this.value = this.todayIso();
      this.open = false;
    },

    clear() {
      this.value = "";
      this.open = false;
    },
  }));
}
